import { useEffect, useState } from "react";
import Temp from "../../assets/newicons/temp.svg";
import Blood from "../../assets/newicons/blood1.svg";
import Res from "../../assets/newicons/respirate.png";
import Pulse from "../../assets/newicons/pulse.svg";
import Heart from "../../assets/newicons/heart.svg";
import Height from "../../assets/newicons/height_2.svg";
import Weight from "../../assets/newicons/monitor_weight_2.svg";
import BMI from "../../assets/newicons/bmi.svg";
import Watch from "../../assets/Icons/vital/watch.svg";

import {
  AreaChart,
  Tooltip,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import VitalService from "../../Services/VitalService";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Modal } from "antd";

import PulseOXChart from "../../components/Activities/PulseOXChart";
import HeartRateChart from "../../components/Activities/HeartRateChart";
import RespiratoryChart from "../../components/Activities/RespiratoryChart";

dayjs.extend(isSameOrBefore);

const AllAndRPMVitals = (props) => {
  const { vitalsData, rpmVitals, showVitals, index, patientData } = props;

  const patObj = JSON.parse(localStorage.getItem("patientData") || "{}");
  let smartWatchId = patientData[0]?.smartWatchUserId;

  const visibleCards = 3;

  const vitalsConfig = [
    {
      name: "Temperature",
      code: "TP",
      unit: "°F",
      color: "#24CC8C",
      bg: "#E9FFEE",
      icon: Temp,
      rpmKey: "temperature",
      showWatch: false,
    },
    {
      name: "Blood Pressure",
      code: ["BPS", "BPD"],
      unit: "mmHg",
      color: "#FF9E9E",
      bg: "#FFE2E8",
      icon: Blood,
      rpmKey: ["systolic", "diastolic"],
      showWatch: false,
    },
    {
      name: "Respiratory Rate",
      code: "RR",
      unit: "rpm",
      color: "#9DCBFF",
      bg: "#E1EDFF",
      icon: Res,
      rpmKey: "respiratoryRate",
      showWatch: smartWatchId ? true : false,
    },
    {
      name: "Pulse Ox.",
      code: "SPO2",
      unit: "%",
      color: "#A8ECFF",
      bg: "#DFF8FF",
      icon: Pulse,
      rpmKey: "spo2",
      showWatch: smartWatchId ? true : false,
    },
    {
      name: "Heart Rate",
      code: "HR",
      unit: "bpm",
      color: "#FFD3B4",
      bg: "#FFE9DA",
      icon: Heart,
      rpmKey: "heartRate",
      showWatch: smartWatchId ? true : false,
    },
    {
      name: "Height",
      code: "HI",
      unit: "cm",
      color: "#DDC0FF",
      bg: "#FFF5D2",
      icon: Height,
      rpmKey: "height",
      showWatch: false,
    },
    {
      name: "Weight",
      code: "WE",
      unit: "kg",
      color: "#FFC0D7",
      bg: "#FFE9F3",
      icon: Weight,
      rpmKey: "weight",
      showWatch: false,
    },
    {
      name: "BMI",
      code: "BMI",
      unit: "kg/m²",
      color: "#FFEDC2",
      bg: "#FAECFF",
      icon: BMI,
      rpmKey: "bmi",
      showWatch: false,
    },
  ];

  const shouldShowVital = (vital) => {
    if (showVitals === "ALL") return true;

    if (showVitals === "RPM") {
      if (Array.isArray(vital.rpmKey)) {
        return vital.rpmKey.some((k) => rpmVitals?.[k] === 1);
      }
      return rpmVitals?.[vital.rpmKey] === 1;
    }

    return true;
  };
  const [showChart, setShowChart] = useState("");
  const [chartModal, setChartModal] = useState(false);

  const onChartClose = () => {
    setShowChart("");
    setChartModal(false);
  };

  const handleTypeCharOpen = (code) => {
    setShowChart(code);
    setChartModal(true);
  };

  function getNextValidVital(vitalsData, code) {
    // For single-code vitals
    if (!Array.isArray(code)) {
      const list = vitalsData.filter(
        (v) => v.vitalCode === code && v.value !== 0 && v.value !== "0",
      );
      return list.length ? list[0]?.value : "";
    }

    // For BP (dual codes)
    const systolic = vitalsData.filter(
      (v) => v.vitalCode === "BPS" && v.value !== 0 && v.value !== "0",
    );
    const diastolic = vitalsData.filter(
      (v) => v.vitalCode === "BPD" && v.value !== 0 && v.value !== "0",
    );

    return `${systolic[0]?.value ?? ""}/${diastolic[0]?.value ?? ""}`;
  }

  return (
    <div className="overflow-hidden">
      <div
        className="flex transition-transform duration-500 ease-in-out gap-4"
        style={{
          transform: `translateX(-${index * (100 / visibleCards)}%)`,
          width: `${(vitalsConfig.length / visibleCards) * 100}%`,
        }}
      >
        {vitalsConfig.map((vital, idx) => {
          if (!shouldShowVital(vital)) return null;

          let value = getNextValidVital(vitalsData, vital.code);

          return (
            <div
              key={idx}
              className="bg-white p-2 shadow-sm w-52 h-40 border rounded-md flex-shrink-0"
            >
              <div className="flex gap-3 items-center mb-2">
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: vital.bg }}
                >
                  <img src={vital.icon} alt="" className="w-5 h-5" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-[#333333]">
                    {vital.name}
                  </p>
                  <p className="text-lg font-semibold text-[#333333]">
                    {value}
                    <span className="text-sm font-normal text-gray-400">
                      {" " + vital.unit}
                    </span>
                  </p>
                </div>
                {vital.showWatch && (
                  <div onClick={() => handleTypeCharOpen(vital.code)}>
                    <img
                      src={Watch}
                      alt=""
                      className="w-5 h-5 cursor-pointer"
                      title="Click to View Device Data"
                    />
                  </div>
                )}
              </div>
              <Graph
                code={Array.isArray(vital.code) ? vital.code[0] : vital.code}
                color={vital.color}
                patientId={patObj?.patientId}
              />
            </div>
          );
        })}
      </div>

      <Modal
        open={chartModal}
        onCancel={onChartClose}
        footer={null}
        width={900}
        title={showChart === "SPO2" ? "SpO2" : showChart}
        destroyOnClose
      >
        {showChart === "SPO2" && <PulseOXChart smartWatchId={smartWatchId} />}
        {showChart === "HR" && <HeartRateChart smartWatchId={smartWatchId} />}
        {showChart === "RR" && <RespiratoryChart smartWatchId={smartWatchId} />}
      </Modal>
    </div>
  );
};

const Graph = ({ code, color, patientId }) => {
  const [chartData, setChartData] = useState([]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;

      if (!data) return null;

      const hasDual = data.data1 != null || data.data2 != null;
      const valueText = hasDual
        ? `${data.data1 ?? "-"}${data.data2 ? ` / ${data.data2}` : ""}`
        : (data.uv ?? "-");

      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ccc",
            padding: "8px",
            borderRadius: "4px",
            fontSize: "10px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>
            {dayjs(data.date).format("dddd, YYYY-MM-DD")}
          </p>
          <p style={{ margin: 0, color: "#FB7185" }}>Value: {valueText}</p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      const result = await getVitalsForGraph(code);
      setChartData(result);
    };
    fetchData();
  }, []);

  const getVitalsForGraph = async (code) => {
    const fromDate = dayjs().subtract(7, "day").startOf("day");
    const toDate = dayjs().endOf("day");
    const vitalCode = code === "BPS" ? ["BPS", "BPD"] : [code];
    const rangePayload = {
      patientId,
      vitalCode: vitalCode,
      fromDate: fromDate.format("MM/DD/YYYY"),
      toDate: toDate.format("MM/DD/YYYY"),
      status: 1,
    };
    const result = await VitalService.getVitalForGraph(rangePayload);
    const allVitals = result?.data || [];
    const valuesByCode = {};
    (allVitals || [])
      .filter((x) => x.value && x.value !== 0 && x.value !== "0")
      .forEach((item) => {
        const dayKey = dayjs(item.recordedDateTime).format("YYYY-MM-DD");
        if (!valuesByCode[item.vitalCode]) valuesByCode[item.vitalCode] = {};
        if (valuesByCode[item.vitalCode][dayKey] == null) {
          valuesByCode[item.vitalCode][dayKey] = item.value;
        }
      });
    const data = [];
    let current = fromDate;
    while (current.isSameOrBefore(toDate, "day")) {
      const dateKey = current.format("YYYY-MM-DD");
      data.push({
        day: current.format("dd")[0],
        date: dateKey,
        data1: valuesByCode["BPS"]?.[dateKey] ?? null,
        data2: valuesByCode["BPD"]?.[dateKey] ?? null,
        uv: valuesByCode[code]?.[dateKey] ?? null,
      });
      current = current.add(1, "day");
    }

    return data;
  };

  // const getVitalsForGraph = async (code) => {
  //   const fromDate = dayjs().subtract(7, "day").startOf("day");
  //   const toDate = dayjs().endOf("day");

  //   const vitalCodes = code === "BPS" ? ["BPS", "BPD"] : [code];
  //   const dateTimePayload = {
  //     patientId,
  //     vitalCodes,
  //     fromDate: fromDate.format("MM/DD/YYYY"),
  //     toDate: toDate.format("MM/DD/YYYY"),
  //   };

  //   const recordDateTime = await VitalService.getVitalDateTime(dateTimePayload);
  //   const records = recordDateTime?.data || [];

  //   let allVitals = [];
  //   if (records.length) {
  //     const fetches = records.map((rec) => {
  //       const recorded = dayjs(rec.recordedDateTime).format(
  //         "YYYY-MM-DD HH:mm:ss",
  //       );
  //       const p = {
  //         patientId,
  //         vitalCodes,
  //         fromDate: recorded,
  //         toDate: recorded,
  //       };
  //       return VitalService.getVitalsData(p).then((r) => r?.data || []);
  //     });

  //     const fetched = await Promise.all(fetches);
  //     allVitals = fetched.flat();
  //   } else {
  //     const rangePayload = {
  //       patientId,
  //       vitalCodes,
  //       fromDate: fromDate.format("YYYY-MM-DD HH:mm:ss"),
  //       toDate: toDate.format("YYYY-MM-DD HH:mm:ss"),
  //     };
  //     const result = await VitalService.getVitalsData(rangePayload);
  //     allVitals = result?.data || [];
  //   }

  //   const valuesByCode = {};

  //   (allVitals || [])
  //     .filter((x) => x.value && x.value !== 0 && x.value !== "0")
  //     .forEach((item) => {
  //       const dayKey = dayjs(item.recordedDateTime).format("YYYY-MM-DD");
  //       if (!valuesByCode[item.vitalCode]) valuesByCode[item.vitalCode] = {};
  //       if (valuesByCode[item.vitalCode][dayKey] == null) {
  //         valuesByCode[item.vitalCode][dayKey] = item.value;
  //       }
  //     });

  //   const data = [];
  //   let current = fromDate;
  //   while (current.isSameOrBefore(toDate, "day")) {
  //     const dateKey = current.format("YYYY-MM-DD");
  //     data.push({
  //       day: current.format("dd")[0],
  //       date: dateKey,
  //       data1: valuesByCode["BPS"]?.[dateKey] ?? null,
  //       data2: valuesByCode["BPD"]?.[dateKey] ?? null,
  //       uv: valuesByCode[code]?.[dateKey] ?? null,
  //     });
  //     current = current.add(1, "day");
  //   }

  //   return data;
  // };

  return (
    <div className="w-full h-[100px] flex items-center justify-center">
      {chartData?.length ? (
        <ResponsiveContainer width="100%" height={100}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#374151", fontSize: 10 }}
            />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} />

            <Area
              type="basis"
              dataKey="uv"
              stroke={color}
              strokeWidth={2}
              fill={color}
              fillOpacity={0.2}
              connectNulls={true}
              dot={(props) => {
                if (props.payload.uv != null) {
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={4} // size of the dot
                      fill={color} // dot color
                      stroke="#fff" // outline
                      strokeWidth={1}
                    />
                  );
                }
                return null;
              }}
            />
            <Area
              type="basis"
              dataKey="data2"
              stroke="#82ca9d"
              strokeWidth={2}
              fill={"#82ca9d"}
              fillOpacity={0.3}
              connectNulls
              dot={(props) =>
                props.payload.data2 != null ? (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={4}
                    fill="#82ca9d"
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ) : null
              }
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          <div className="w-full h-[100px] bg-gray-200 rounded-md animate-pulse" />
        </div>
      )}
    </div>
  );
};

export default AllAndRPMVitals;
